import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocationsEnabledToPost } from "../../api/socialLocationPostsApi";
import translate from "../../config/translations/translate";
import { setLoading } from "../../commons/components/application/miscActions";
import { UxSelect } from "botbit-ui-components";
import { envConfig } from "../../config/config";
import Swal from "sweetalert2";

const SocialLocationPostLocationsSelect = ({
  onChange,
  socialLocationId: defaultSelected,
  t
}) => {
  const company = useSelector((state) => state.application.selectedCompany);
  const dispatch = useDispatch();
  const [locations, setLocations] = React.useState();
  const [options, setOptions] = React.useState();
  const [isValidating, setIsValidating] = React.useState(false);

  const showOptInModal = (hasEnabledAccounts = false, showContinue = false) => {
    const title = hasEnabledAccounts ? t('someLocationsRequireReconnection.title') : t('googleAccountsExpired.title');
    const message = hasEnabledAccounts ? t('someLocationsRequireReconnection.message') : t('googleAccountsExpired.message');
    const description = hasEnabledAccounts ? t('someLocationsRequireReconnection.description') : t('googleAccountsExpired.description');
    
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: left;">
          <p><strong>${message}</strong></p>
          <p>${description}</p>
        </div>
      `,
      icon: 'warning',
      confirmButtonText: t('googleAccountsExpired.reconnectNow'),
      showCancelButton: showContinue,
      cancelButtonText: showContinue ? t('someLocationsRequireReconnection.continuePosting') : undefined,
      confirmButtonColor: '#007bff',
      cancelButtonColor: '#6c757d',
      allowOutsideClick: false,
      allowEscapeKey: true,
      showCloseButton: true
    }).then((result) => {
      if (result.value) {
        window.location.href = `${envConfig.v2SettingsUrl}?tab=locations`;
      } else if (result.dismiss === Swal.DismissReason.close) {
        window.history.back();
      }
    });
  };

  useEffect(() => {
    if (company) {
      dispatch(setLoading(true));
      setIsValidating(true);
      getLocationsEnabledToPost(company.id).then((res) => {
        const data = res.data.data;
        setLocations(data);
        
        // Filtrar solo cuentas habilitadas
        const enabledAccounts = data.filter(account => account.enableToPost);
        const disabledAccounts = data.filter(account => !account.enableToPost && account.socialLocationIds && account.socialLocationIds.length > 0);
        
               if (enabledAccounts.length === 0) {
                 dispatch(setLoading(false));
                 setIsValidating(false);
                 showOptInModal(false, false); // Sin cuentas habilitadas, sin botón continuar
                 return;
               }
               
               // Si hay cuentas deshabilitadas, mostrar modal informativo
               if (disabledAccounts.length > 0) {
                 showOptInModal(true, true); // Hay cuentas habilitadas pero también deshabilitadas, con botón continuar
               }
        
        setOptions(
          enabledAccounts
            .map((account) =>
              account.socialLocationIds.map((socialLocationId) => {
                return {
                  text:
                    socialLocationId.storeName +
                    " - " +
                    socialLocationId.name,
                  value: socialLocationId.id,
                  selected: -1 === defaultSelected || socialLocationId.id === defaultSelected,
                };
              })
            )
            .flat()
        );
        
        onChange(enabledAccounts
          .map((account) => ({
            ...account,
            socialLocationIds: account.socialLocationIds.filter(
              (socialLocationId) => defaultSelected===socialLocationId.id
            ),
          }))
          .filter((account) => account.socialLocationIds.length > 0));
        dispatch(setLoading(false));
        setIsValidating(false);
             }).catch((error) => {
               console.error('Error loading locations:', error);
               dispatch(setLoading(false));
               setIsValidating(false);
               if (error.response && error.response.status === 403) {
                 Swal.fire({
                   title: t('noPermission.title'),
                   html: `<p>${t('noPermission.message')}</p>`,
                   icon: 'error',
                   confirmButtonText: t('ok'),
                   confirmButtonColor: '#007bff',
                 }).then(() => window.history.back());
               } else {
                 showOptInModal(false, false);
               }
             });
    }
  }, []);

  // Mostrar indicador de carga mientras se valida
  if (isValidating) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">{t('validatingGoogleAccounts.loading')}</span>
        </div>
        <p style={{ marginTop: '10px', color: '#6c757d' }}>
          {t('validatingGoogleAccounts.message')}
        </p>
      </div>
    );
  }

  if (!locations || !options) return null;
  return (
    <div>
      <UxSelect
        label={t("selectLocations")}
        options={options}
        onChange={(e) => {
          const selectedLocations = locations
            .filter((account) => account.enableToPost)
            .map((account) => ({
              ...account,
              socialLocationIds: account.socialLocationIds.filter(
                (socialLocationId) => e.includes(socialLocationId.id)
              ),
            }))
            .filter((account) => account.socialLocationIds.length > 0);
          onChange(selectedLocations);
        }}
        selectAll={{ show: true, label: t("selectAll") }}
        className="d-flex"
      />
    </div>
  );
};

SocialLocationPostLocationsSelect.translations = {
  locationNotEnabled: {
    es: "No tienes permiso para postear en este perfil. Por favor solicita que te de permiso a ",
    en: "You don't have permission to post in this profile. Please request permission to ",
  },
  swalText: {
    es: "Publicación creada",
    en: "Post created",
  },
  ok: {
    es: "Aceptar",
    en: "Ok",
  },
  selectAll: {
    es: "Publicar en todos los locales",
    en: "Select all",
  },
  swalErrorText: {
    es: "Error al crear la publicación en alguna ubicación",
    en: "Error creating post in some location",
  },
  seePost: {
    es: "Ver publicación",
    en: "See post",
  },
  errorCreatingPost: {
    es: "Error",
    en: "Error",
  },
  selectLocations:{
    es: "Selecciona las ubicaciones",
    en: "Select locations"
  },
  googleAccountsExpired: {
    title: {
      es: "Cuentas de Google Vencidas",
      en: "Google Accounts Expired"
    },
    message: {
      es: "Todas tus cuentas de Google están vencidas.",
      en: "All your Google accounts are expired."
    },
    description: {
      es: "Para continuar publicando, necesitas reconectar tus cuentas de Google Business Profile.",
      en: "To continue posting, you need to reconnect your Google Business Profile accounts."
    },
    reconnectNow: {
      es: "Reconectar Ahora",
      en: "Reconnect Now"
    }
  },
  someLocationsRequireReconnection: {
    title: {
      es: "Algunas ubicaciones requieren reconexión",
      en: "Some locations require reconnection"
    },
    message: {
      es: "Algunas de tus ubicaciones requieren reconexión.",
      en: "Some of your locations require reconnection."
    },
    description: {
      es: "Puedes continuar publicando con las ubicaciones disponibles, o reconectar todas las cuentas.",
      en: "You can continue posting with available locations, or reconnect all accounts."
    },
    continuePosting: {
      es: "Continuar Publicando",
      en: "Continue Posting"
    }
  },
  validatingGoogleAccounts: {
    loading: {
      es: "Validando cuentas de Google...",
      en: "Validating Google accounts..."
    },
    message: {
      es: "Validando cuentas de Google...",
      en: "Validating Google accounts..."
    }
  },
  noPermission: {
    title: {
      es: "Sin permisos",
      en: "No permissions"
    },
    message: {
      es: "No tenés permisos para crear publicaciones en Google Business Profile. Contactá a tu administrador.",
      en: "You don't have permission to create Google Business Profile posts. Contact your administrator."
    }
  }
};
export default translate(SocialLocationPostLocationsSelect);
